export interface ReceiverHandles {
  context: framework.CastReceiverContext;
  playerManager: framework.PlayerManager;
}

/** Device tier used to pick a playback profile. */
export type DeviceProfile = 'low' | 'high';

export interface BuiltOptions {
  options: framework.CastReceiverOptions;
  profile: DeviceProfile;
  supports4K: boolean;
}

export function initReceiver(): ReceiverHandles {
  const context = cast.framework.CastReceiverContext.getInstance();
  const playerManager = context.getPlayerManager();

  context.setLoggerLevel(
    import.meta.env.DEV ? cast.framework.LoggerLevel.DEBUG : cast.framework.LoggerLevel.INFO,
  );

  return { context, playerManager };
}

/**
 * Probe the device for 4K capability. Cast Ultra, Chromecast with Google TV,
 * Google TV Streamer, and Android TVs all report 4K H.264 / HEVC support.
 * Chromecast 3rd gen (and similar 1080p-max Cast 1/2 hardware) returns false.
 * Kept as a stable signal for future per-device tweaks even though we no
 * longer apply Shaka restrictions to HLS (see below). Shaka still plays
 * non-HLS content such as DASH.
 */
function detectSupports4K(context: framework.CastReceiverContext): boolean {
  try {
    return (
      context.canDisplayType('video/mp4', 'hvc1.1.6.L150.B0') ||
      context.canDisplayType('video/mp4', 'avc1.640033') ||
      context.canDisplayType('video/mp4', 'avc1.640033', 3840, 2160)
    );
  } catch {
    return false;
  }
}

// -----------------------------------------------------------------------------
// Why no Shaka config, and why useShakaForHls is pinned to false
//
// Earlier iterations of this receiver forced Shaka for HLS (useShakaForHls=true)
// so we could apply shakaConfig.restrictions (e.g. maxHeight=720 to keep Cast
// 3rd gen off the mislabelled-level 1080p variants in Sardius multi-audio
// archives). With the current Cast Shaka build (v4.9.2-caf2) shipped to both
// Cast 3rd gen and Google TV Streamer, that path can't reach PLAYING for
// unmuxed-audio HLS — the audio source buffer never initialises. Tested with
// and without mediaSource.forceTransmux=true; with it the player reaches
// PLAYER_LOAD_COMPLETE then stalls, without it the player stalls earlier
// during buffering. Both end with no playback.
//
// CAF 3.0.0156 (rolled out Aug–Sep 2026) made Shaka the HLS default, so MPL
// must now be requested explicitly: left unset, these archives went back to
// Shaka and stalled in BUFFERING again (verified on an LG webOS Cast TV).
//
// Falling back to MPL, which handles HLS natively. Multi-audio
// archives play on both devices, single-audio fMP4 plays on both devices,
// MP4 / MP3 play on both devices. Trade-off: MPL ignores shakaConfig, so
// we can no longer constrain Cast 3rd gen to the 720p variant — it will
// pick the 1080p variant on Sardius multi-audio archives (mislabelled
// H.264 L4.2) and play with periodic audio choppiness. Sardius can resolve
// that at the source by either:
//   1. Re-encoding multi-audio archives with correct H.264 level
//      declarations (or using High Profile instead of Main), or
//   2. Migrating the multi-audio pipeline to fMP4/CMAF segments like the
//      single-audio pipeline already uses (cleaner long-term fix).
// -----------------------------------------------------------------------------

export function buildReceiverOptions(
  context: framework.CastReceiverContext,
): BuiltOptions {
  const options = new cast.framework.CastReceiverOptions();
  options.useShakaForHls = false;

  options.supportedCommands =
    cast.framework.messages.Command.ALL_BASIC_MEDIA | cast.framework.messages.Command.EDIT_TRACKS;

  options.playbackConfig = new cast.framework.PlaybackConfig();
  options.playbackConfig.autoResumeDuration = 5;

  // Profile detection kept for the [sardius:STARTUP] banner so we can spot
  // device tier in chrome://inspect. No behavioural difference yet.
  const supports4K = detectSupports4K(context);
  const profile: DeviceProfile = supports4K ? 'high' : 'low';

  if (import.meta.env.DEV) {
    options.maxInactivity = 3600;
    options.shakaVariant = cast.framework.ShakaVariant.DEBUG;
  }

  return { options, profile, supports4K };
}
